import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../api";
import { usePermissions } from "../../context/PermissionContext";
import "../admins/AdminStaffPage.css";
import "./ManagerTeamUsersPage.css";
import { Empty } from "./teamUserDisplay";

// Manager -> Preventive Maintenance: monthly preventive checks for the
// manager's OWN unit. GET/POST/PUT /manager/preventive-maintenance -- the
// backend checks preventive_maintenance:view/create/update and always uses
// the caller's own unit (services/preventiveMaintenanceManagerService.js).
// This page never sends a unit id. A check is assigned to technicians of
// the unit (only those are offered); checks shown as "All units" are
// global, Admin-managed and read-only here. Technicians mark checks
// completed on their own page -- there is no completion action here, and
// no frequency/active field exists (checks are monthly).

const EMPTY_FORM = { name: "", description: "", tat_value: "30", tat_unit: "days", technician_ids: [] };

const errorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || fallback;

const formatTat = (check) =>
  check.tat_value != null && check.tat_unit ? `${check.tat_value} ${check.tat_unit}` : null;

const ManagerPreventiveMaintenancePage = () => {
  const { can } = usePermissions();
  const canCreate = can("preventive_maintenance", "create");
  const canUpdate = can("preventive_maintenance", "update");

  const [unit, setUnit] = useState(null);
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [formMode, setFormMode] = useState(null); // null | "create" | "edit"
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [technicians, setTechnicians] = useState([]);
  const [techniciansLoading, setTechniciansLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchChecks = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const res = await api.get("/manager/preventive-maintenance");
      setUnit(res.data?.data?.unit || null);
      setChecks(Array.isArray(res.data?.data?.checks) ? res.data.data.checks : []);
    } catch (err) {
      setChecks([]);
      setLoadError(errorMessage(err, "Failed to load preventive maintenance."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChecks();
  }, [fetchChecks]);

  // Own-unit active technicians only -- the backend builds this list.
  const loadTechnicians = async () => {
    try {
      setTechniciansLoading(true);
      const res = await api.get("/manager/preventive-maintenance/technicians");
      setTechnicians(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setTechnicians([]);
      toast.error(errorMessage(err, "Failed to load technicians."));
    } finally {
      setTechniciansLoading(false);
    }
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormMode("create");
    loadTechnicians();
  };

  const openEdit = (check) => {
    setForm({
      name: check.name || "",
      description: check.description || "",
      tat_value: check.tat_value != null ? String(check.tat_value) : "",
      tat_unit: check.tat_unit || "days",
      technician_ids: (check.technicians || []).map((t) => Number(t.id)),
    });
    setEditingId(check.id);
    setFormMode("edit");
    loadTechnicians();
  };

  const closeForm = () => {
    if (saving) return;
    setFormMode(null);
    setEditingId(null);
  };

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const toggleTechnician = (id) =>
    setForm((prev) => ({
      ...prev,
      technician_ids: prev.technician_ids.includes(id)
        ? prev.technician_ids.filter((t) => t !== id)
        : [...prev.technician_ids, id],
    }));

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Activity name is required.");
    if (!form.tat_value || Number(form.tat_value) <= 0) return toast.error("TAT must be a positive number.");
    if (!form.technician_ids.length) return toast.error("Select at least one technician.");

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      tat_value: Number(form.tat_value),
      tat_unit: form.tat_unit,
      technician_ids: form.technician_ids,
    };
    try {
      setSaving(true);
      if (formMode === "create") {
        await api.post("/manager/preventive-maintenance", payload);
        toast.success("Preventive maintenance check created.");
      } else {
        await api.put(`/manager/preventive-maintenance/${editingId}`, payload);
        toast.success("Preventive maintenance check updated.");
      }
      setFormMode(null);
      setEditingId(null);
      fetchChecks();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save the preventive maintenance check."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="unit-page admin-tasks-theme team-users-page">
      <div className="unit-header">
        <div>
          <div className="unit-page-label">Manager</div>
          <h2>Preventive Maintenance</h2>
          <p>Monthly preventive checks for your unit&rsquo;s technicians. Technicians mark them completed on their own page.</p>
        </div>

        <div className="unit-header-actions">
          <button className="unit-btn unit-btn-light" onClick={fetchChecks} disabled={loading}>
            <i className={`fa-solid fa-rotate${loading ? " fa-spin" : ""}`} aria-hidden="true"></i>
            {loading ? "Refreshing..." : "Refresh"}
          </button>

          {canCreate && (
            <button className="unit-btn unit-btn-primary" onClick={openCreate}>
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              Add Preventive Maintenance
            </button>
          )}
        </div>
      </div>

      <div className="unit-card">
        <div className="unit-toolbar">
          <div>
            <h5>
              <i className="fa-solid fa-shield-halved staff-list-icon" aria-hidden="true"></i>
              Preventive Checks{unit?.name ? ` — ${unit.name}` : ""}
            </h5>
            <p>Only your unit&rsquo;s checks can be edited. &ldquo;All units&rdquo; checks are managed by an administrator.</p>
          </div>
        </div>

        <div className="unit-table-wrapper" role="region" aria-label="Preventive maintenance table" tabIndex={0}>
          {loading ? (
            <div className="unit-loader">
              <div className="spinner-border" role="status"></div>
            </div>
          ) : loadError ? (
            <div className="unit-empty" role="alert">
              <div className="unit-empty-icon">
                <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
              </div>
              <p>{loadError}</p>
              <button className="unit-btn unit-btn-light" onClick={fetchChecks}>
                Try again
              </button>
            </div>
          ) : (
            <table className="table unit-table tu-table">
              <thead>
                <tr>
                  <th scope="col" className="tu-col-sno">S.NO.</th>
                  <th scope="col">Activity</th>
                  <th scope="col">Description</th>
                  <th scope="col">TAT</th>
                  <th scope="col">Applies To</th>
                  <th scope="col" className="tu-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {checks.length ? (
                  checks.map((check, index) => (
                    <tr key={check.id}>
                      <td className="tu-sno">{index + 1}</td>
                      <td><strong>{check.name}</strong></td>
                      <td>{check.description || <Empty />}</td>
                      <td>{formatTat(check) || <Empty />}</td>
                      <td>
                        {check.scope === "UNIT"
                          ? (check.technicians || []).map((t) => t.name).join(", ")
                          : <span className="badge text-bg-secondary">All units</span>}
                      </td>
                      <td className="tu-col-actions">
                        <div className="unit-actions">
                          {check.editable && canUpdate ? (
                            <button
                              className="unit-icon-btn unit-edit staff-action-edit"
                              onClick={() => openEdit(check)}
                              title="Edit"
                              aria-label={`Edit ${check.name}`}
                            >
                              <i className="fa-solid fa-pen" aria-hidden="true"></i>
                            </button>
                          ) : (
                            <Empty />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>
                      <div className="unit-empty">
                        <div className="unit-empty-icon">
                          <i className="fa-solid fa-shield-halved" aria-hidden="true"></i>
                        </div>
                        <p>No preventive maintenance checks yet.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {formMode && (
        <div className="modal show fade d-block unit-modal" tabIndex="-1" style={{ background: "rgba(74, 14, 34, 0.55)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered admin-teal-modal admin-teal-modal-lg" role="dialog" aria-modal="true" aria-labelledby="pm-form-title">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="pm-form-title">
                  {formMode === "create" ? "Add Preventive Maintenance" : "Edit Preventive Maintenance"}
                </h5>
                <button type="button" className="btn-close" onClick={closeForm} title="Close" aria-label="Close"></button>
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label" htmlFor="pm-name">Activity Name</label>
                    <input id="pm-name" className="form-control" placeholder="e.g. UPS Battery Check" value={form.name} onChange={(e) => handleChange("name", e.target.value)} />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label" htmlFor="pm-unit">Unit</label>
                    <input id="pm-unit" className="form-control" value={unit?.name || "Your unit"} readOnly disabled />
                  </div>

                  <div className="col-12">
                    <label className="form-label" htmlFor="pm-description">Description</label>
                    <textarea id="pm-description" className="form-control" rows={3} placeholder="What the technician should do each month" value={form.description} onChange={(e) => handleChange("description", e.target.value)} />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label" htmlFor="pm-tat-value">TAT</label>
                    <input id="pm-tat-value" type="number" min="1" className="form-control" value={form.tat_value} onChange={(e) => handleChange("tat_value", e.target.value)} />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label" htmlFor="pm-tat-unit">TAT Unit</label>
                    <select id="pm-tat-unit" className="form-select" value={form.tat_unit} onChange={(e) => handleChange("tat_unit", e.target.value)}>
                      <option value="days">Days</option>
                      <option value="hours">Hours</option>
                    </select>
                  </div>

                  <div className="col-12">
                    <span className="form-label d-block" id="pm-technicians-label">Assigned Technicians</span>
                    {techniciansLoading ? (
                      <div className="spinner-border spinner-border-sm" role="status"></div>
                    ) : technicians.length ? (
                      <div className="row g-2" role="group" aria-labelledby="pm-technicians-label">
                        {technicians.map((t) => (
                          <div className="col-md-6" key={t.id}>
                            <div className="form-check">
                              <input
                                id={`pm-tech-${t.id}`}
                                type="checkbox"
                                className="form-check-input"
                                checked={form.technician_ids.includes(Number(t.id))}
                                onChange={() => toggleTechnician(Number(t.id))}
                              />
                              <label className="form-check-label" htmlFor={`pm-tech-${t.id}`}>{t.name}</label>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted mb-0">No active technicians in your unit.</p>
                    )}
                    <small className="text-muted d-block mt-1">Monthly check. Only these technicians will see it.</small>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button className="unit-btn unit-btn-light" onClick={closeForm} disabled={saving}>
                  Cancel
                </button>
                <button className="unit-btn unit-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving..." : formMode === "create" ? "Create" : "Update"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerPreventiveMaintenancePage;
